import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  Cog6ToothIcon,
  HomeIcon,
  FaceSmileIcon,
  XMarkIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { classNames } from '../utils';
import { useGetUser } from '../hooks/api-hooks';
import { useAppProvider } from '../app-provider';
import ViewCurrentNeofaceSelection from './view-persona';
import Logo from '../../public/images/logo.svg';

const navigation = [
  { name: 'Dashboard', to: 'dashboard', icon: HomeIcon },
  { name: 'Anonymize Content', to: 'anonymize-content', icon: ArrowPathIcon },
  { name: 'Create Neoface', to: 'create-neoface', icon: FaceSmileIcon },
  { name: 'My Neofaces', to: 'my-neofaces', icon: IdentificationIcon },
  { name: 'Settings', to: 'settings', icon: Cog6ToothIcon },
  { name: 'Learn', to: 'learn', icon: AcademicCapIcon },
  { name: 'Support', to: 'support', icon: QuestionMarkCircleIcon },
  { name: 'Feedback', to: 'feedback', icon: ChatBubbleLeftRightIcon },
];

export default function Sidebar() {
  const { sidebarOpen, setSidebarOpen } = useAppProvider()
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => {
    setSidebarOpen(false);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);
  const location = useLocation();
  const user = useGetUser()

  const UsageBar = ({ usage, limit }: { usage: number, limit: number }) => {
    const isUnlimited = limit === -1;
    const percentage = isUnlimited ? 100 : (usage / limit) * 100;

    const barColor =
      isUnlimited ? 'bg-gradient-to-r from-purple-400 via-pink-500 to-red-500' :
        percentage > 90 ? 'bg-red-500' :
          percentage > 50 ? 'bg-yellow-500' :
            'bg-green-500';

    return (
      <div className="relative h-2 mt-1 bg-gray-700 rounded-full overflow-hidden">
        {isUnlimited ? (
          <div
            className={`absolute top-0 left-0 h-full w-[200%] ${barColor} animate-flow-gradient`}
          />
        ) : (
          <div
            className={`absolute top-0 left-0 h-full rounded-full ${barColor}`}
            style={{ width: `${percentage}%` }}
          />
        )}
      </div>
    );
  };

  const UsageItem = ({ item }: { item: {
      name: string;
      usage: number;
      limit: number;
    }}) => {
    const isUnlimited = item.limit === -1;

    return (
      <li className="text-indigo-200">
        <div className="flex justify-between text-sm font-semibold">
          <span>{item.name === 'videos' ? 'videos (seconds)' : item.name}</span>
          <span>{isUnlimited ? 'Unlimited' : `${item.usage} / ${item.limit}`}</span>
        </div>
        <UsageBar usage={item.usage} limit={item.limit} />
      </li>
    );
  };

  const renderUsageSection = () => (
    <div className="mt-6">
      <div className='text-xs font-semibold leading-6 text-indigo-200'>
        Usage &nbsp;
        <span
          className='inline-flex items-center rounded-full bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10'>
        {user.data?.subscription.label}
      </span>
      </div>
      <ul className='mt-4 space-y-2'>
        {user.data?.subscription.features.map((item) => (
          <UsageItem key={item.name} item={item} />
        ))}
      </ul>
    </div>
  );


  const renderSidebar = () => (
    <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-gray-800 px-6 pb-4'>
      <div className='flex h-16 shrink-0 items-center'>
        <ViewCurrentNeofaceSelection isOpen={isModalOpen} onClose={closeModal} imageUrl={user.data?.persona?.url || ''} />
        <Link to='/dashboard'>
          <img src={Logo} className='h-8 w-8'/>
        </Link>
      </div>
      <nav className='flex flex-1 flex-col'>
        <ul role='list' className='flex flex-1 flex-col gap-y-7'>
          <li>
            <ul role='list' className='-mx-2 space-y-1'>
              {navigation.map((item) => (
                <li key={item.name} className={`${item.to}`}>
                  <Link
                    onClick={() => setSidebarOpen(false)}
                    to={item.to}
                    className={classNames(
                      location.pathname === `/${item.to}`
                        ? 'bg-indigo-700 text-white'
                        : 'text-indigo-200 hover:text-white hover:bg-indigo-700',
                      'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                    )}
                  >
                    <item.icon
                      className={classNames(
                        location.pathname === `/${item.to}` ? 'text-white' : 'text-indigo-200 group-hover:text-white',
                        'h-6 w-6 shrink-0',
                      )}
                      aria-hidden='true'
                    />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          <li>
            <div className='text-xs font-semibold leading-6 text-indigo-200'>My Neoface</div>
            {user.data?.persona.id ? (
              <div
                className='w-20 h-20 mt-2 rounded-full overflow-hidden cursor-pointer transition-transform duration-200 ease-in-out hover:scale-105'
                onClick={openModal}
              >
                <img
                  src={user.data?.persona?.url || ''}
                  alt='Neoface'
                  className='w-full h-full object-cover'
                />
              </div>
            ) : (
              <Link to='/create-neoface'>
                <div
                  className='mt-2 flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 shadow-lg transition duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-105 group'>
                  <svg
                    className='h-10 w-10 text-white transition duration-300 ease-in-out transform group-hover:scale-0'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <span
                    className='absolute text-base text-white text-center opacity-0 transition duration-300 ease-in-out transform group-hover:opacity-100'>
                    My Neoface
                  </span>
                </div>
              </Link>
            )}
          </li>
        </ul>
      </nav>
      {renderUsageSection()}
    </div>
  );

  return (
    <>
      <div>
        {/* Static Sidebar for Mobile*/}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as='div' className='relative z-50 lg:hidden' onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter='transition-opacity ease-linear duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition-opacity ease-linear duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-gray-900/80' />
            </Transition.Child>

            <div className='fixed inset-0 flex'>
              <Transition.Child
                as={Fragment}
                enter='transition ease-in-out duration-300 transform'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transition ease-in-out duration-300 transform'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'
              >
                <Dialog.Panel className='relative mr-16 flex w-full max-w-xs flex-1'>
                  <Transition.Child
                    as={Fragment}
                    enter='ease-in-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <div className='absolute left-full top-0 flex w-16 justify-center pt-5'>
                      <button type='button' className='-m-2.5 p-2.5'
                              onClick={() => setSidebarOpen(false)}>
                        <span className='sr-only'>Close sidebar</span>
                        <XMarkIcon className='h-6 w-6 text-white' aria-hidden='true' />
                      </button>
                    </div>
                  </Transition.Child>
                  {renderSidebar()}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>
        {/* Static sidebar for desktop */}
        <div className='hidden lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col h-full'>
          {/*<div className='flex justify-between mb-10 pr-3 sm:px-2'>*/}
          {renderSidebar()}
        </div>

      </div>
    </>
  );
}